import { Component, OnInit, OnDestroy } from '@angular/core';
import { auditTime } from 'rxjs/operators';
import { Subscription, combineLatest } from 'rxjs';
import { startWith, debounceTime } from 'rxjs/operators';
import moment from 'moment';
import {
  FilterDrawerLayoutComponentBase,
  FilterDrawerLayoutOptions,
  FilterDrawerReorderableLayoutOptions,
} from '../../../classes/filter-drawer-layout-component.class';

@Component({
  selector: 'app-client-filter-drawer-layout',
  templateUrl: '../../default/filter-drawer-layout/filter-drawer-layout.component.html',
  styleUrls: ['../../default/filter-drawer-layout/filter-drawer-layout.component.scss'],
})
export class default_FilterDrawerLayoutComponent extends FilterDrawerLayoutComponentBase implements OnInit, OnDestroy {
  protected getCheckboxFields(): string[] {
    return ['mapFeatures', 'matauranga', 'monitoring', 'observation', 'mapGroups'];
  }

  /**
   * Returns an array of form control names for multi-select fields.
   * These fields will be excluded from automatic filter application
   * @returns Array of form control names.
   */
  protected getMultipleSelectFields(): string[] {
    return [
      'selectedLocations',
      'selectedEnvironmentalDataSources',
      'selectedEnvironmentalDataSourceDashGroups',
      'selectedContributingOrganisations',
      'selectedContributingProjectListItems',
    ];
  }

  /**
   * Returns an array of form control names for fields that require manual application of filters.
   * @returns Array of form control names.
   */
  protected getManualApplyFields(): string[] {
    return ['selectedEnvironmentalDataSources', 'selectedEnvironmentalDataSourceDashGroups'];
  }

  public environmentalApplyDisabled: boolean = true;
  public applyingEnvironmentalFilters: boolean = false;

  private environmentalControlsSub: Subscription | null = null;

  private reorderLock = false;

  orderedMapFeaturesOptions: FilterDrawerLayoutOptions<string> = [];
  reorderBusy: boolean = false;

  /**
   * Return the authoritative options array used for ordered display.
   */
  private getOrderedOptions(): FilterDrawerLayoutOptions<string> {
    return this.orderedMapFeaturesOptions.length ? this.orderedMapFeaturesOptions : this.mapFeaturesOptions || [];
  }

  /**
   * Get the currently selected map-layer keys in the order they appear in the form value.
   * @returns Currently selected map-layer keys in the order they appear in the form value.
   */
  private getSelectedMapGroups(): string[] | null {
    if (!this.filterForm) return null;
    const selectedMapGroups = Object.keys(this.filterForm?.value?.mapGroups || {})
      .filter((key) => this.filterForm?.value?.mapGroups[key])
      .map((key) => String(key));
    return selectedMapGroups;
  }

  /**
   * Central helper to persist and apply a new map-layer ordering/selection.
   *
   * By default this will save the custom order, persist the selection to
   * localStorage, update the FilterService (preserving non-map filters), and
   * recompute ordered options.
   *
   * Options allow callers to skip re-saving the custom order or skipping the
   * localStorage persist when those would undo a previous reset/enforce.
   */
  private applyOrder(
    newMapLayerOrder: string[] | null,
    newMapGroups: string[] | null,
    opts: { saveCustomOrder?: boolean; saveLocalSelection?: boolean } = {
      saveCustomOrder: true,
      saveLocalSelection: true,
    }
  ): void {
    try {
      console.debug('[map-order] applyOrder called with order=', newMapLayerOrder, ' mapGroups=', newMapGroups);

      if (!this.site) return;
      const saveCustom = opts.saveCustomOrder !== false;
      const saveLocal = opts.saveLocalSelection !== false;

      if (newMapLayerOrder) {
        if (saveCustom) {
          try {
            this.saveCustomMapLayerOrder(newMapLayerOrder);
          } catch (e) {
            console.debug('Error saving custom map layer order from applyOrder', e);
          }
        }

        if (saveLocal) {
          try {
            this.saveMapLayerSelectionToLocalStorage(newMapLayerOrder);
          } catch (e) {
            console.debug('Error saving map layer selection from applyOrder', e);
          }
        }
      }

      if (newMapGroups) {
        try {
          this.saveMapGroupSelectionToLocalStorage(newMapGroups);
        } catch (e) {
          console.debug('Error saving map layer selection from applyOrder', e);
        }
      }

      const filtersToUpdate: { [key: string]: any } = {};

      if (newMapLayerOrder) {
        const canonicalOrder = [...newMapLayerOrder];
        filtersToUpdate.mapFeatures = this.reorderPreserveOtherFilters(canonicalOrder);
        filtersToUpdate.mapFeaturesOrder = [...canonicalOrder];
      }

      if (newMapGroups) {
        filtersToUpdate.mapGroups = [...newMapGroups];
      }

      if (Object.keys(filtersToUpdate).length) {
        try {
          this.myFilterService.setFilters(filtersToUpdate);
        } catch (e) {
          console.debug('Error setting filters from applyOrder', e);
        }
      }

      try {
        this.computeOrderedOptions();
      } catch (e) {
        console.debug('Error computing ordered options from applyOrder', e);
      }
    } catch (e) {
      console.debug('Error in applyOrder', e);
    }
  }

  /**
   * Check if two string arrays are equal in terms of order and contents.
   *
   * @param a The first string array or null.
   * @param b The second string array or null.
   * @returns True if both arrays are equal, false otherwise.
   */
  private arraysEqual(a: string[] | null, b: string[] | null): boolean {
    if (!a && !b) return true;
    if (!a || !b) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
    return true;
  }

  /**
   * Persist the mapFeatures selection to localStorage when the form value changes.
   *
   * This method is called whenever the mapFeatures field in the reactive form changes.
   * It saves the selected map features to localStorage for the current site, allowing
   * the selection to be remembered across sessions. It respects the suppressLocalPersistence
   * flag from the base class to avoid persisting during programmatic updates.
   *
   * @param mapFeatures The current map features selected in the form.
   * @returns
   */
  protected onLocalMapFeaturesChange(mapFeatures: string[] | null): void {
    try {
      if (!this.site || !mapFeatures) return;
      if ((this as any).suppressLocalPersistence) return;
      const availableKeys = (this.mapFeaturesOptions || []).map((o) => String(o.key));
      const filtered = mapFeatures.filter((k) => availableKeys.includes(k));
      if (filtered && filtered.length) {
        this.saveMapLayerSelectionToLocalStorage(filtered);
      }
    } catch (e) {
      console.debug('Error persisting mapFeatures from onLocalMapFeaturesChange', e);
    }
  }

  /**
   * Track by function for ngFor to optimize rendering.
   *
   * Prefers the reactive form value for selection state so that toggling a
   * checkbox immediately affects the identity. Falls back to isSelected()
   * (FilterService) when the form is not yet initialized.
   *
   * @param _index The index of the item in the list.
   * @param item The item being tracked.
   * @returns A unique key for the item.
   */
  trackByKey(_index: number, item: any): string {
    const key = String(item?.key ?? item);
    let selFlag = 'unsel';
    try {
      const mv = this.filterForm?.value?.mapFeatures;
      if (mv) {
        if (Array.isArray(mv)) {
          selFlag = mv.includes(key) ? 'sel' : 'unsel';
        } else if (typeof mv === 'object') {
          selFlag = mv[key] ? 'sel' : 'unsel';
        } else {
          selFlag = this.isSelected ? (this.isSelected(key) ? 'sel' : 'unsel') : 'unsel';
        }
      } else {
        selFlag = this.isSelected ? (this.isSelected(key) ? 'sel' : 'unsel') : 'unsel';
      }
    } catch (e) {
      selFlag = 'unsel';
    }
    return `${key}:${selFlag}`;
  }

  /**
   * Called when a checkbox toggles.
   *
   * Force a change detection cycle to ensure
   * the inline controls reflect the updated form state immediately.
   * Then, after a brief delay to allow the form to update, compute the new
   * displayed selected map-layer keys and derive the canonical order.
   * Persist the new order and selection, and update the FilterService so the
   * MapView redraws layers via its filter subscriptions.
   * Respects the suppressLocalPersistence flag from the base class to avoid
   * interfering during programmatic updates.
   */
  public onCheckboxToggle(key: any, context: string): void {
    try {
      (this as any).cdr?.detectChanges();
      try {
        if (!this.site) return;
        if ((this as any).suppressLocalPersistence) return;
        setTimeout(() => {
          try {
            const displayedSelected = this.getActiveMapFeatureOptions().map((o) => String(o.key));
            const copyDisplay = [...displayedSelected];

            // Reverse the order for canonical this is the order expected by applyOrder
            let canonical = copyDisplay.slice().reverse();

            if (context === 'inactive') {
              // we are adding a key to the the active state. So insert it based on the default index
              // order of the existing items.
              // use an insertion sort approach to preserve the existing order as much as possible.

              // find the default index of the key being added
              const defaultZIndex = this.mapFeaturesOptions.find((o) => String(o.key) === key)?.defaultZIndex;

              // at this stage the key is already in the canonical array so remove it first
              // this is awkward angular stuff
              canonical = canonical.filter((k) => k !== key);

              if (defaultZIndex) {
                let insertPos = 0;
                // interate through the canonical array to find the first item with a lower zindex
                for (let i = canonical.length - 1; i > -1; i--) {
                  // if this it the first item with a lower zindex then we have found our insert position
                  const foundItemZIndex = this.mapFeaturesOptions.find(
                    (o) => String(o.key) === canonical[i]
                  )?.defaultZIndex;
                  if (foundItemZIndex !== undefined && foundItemZIndex < defaultZIndex) {
                    break;
                  }
                  insertPos = i;
                }
                // insert the key into the array at insertPos, moving other items down
                canonical.splice(insertPos, 0, key);
              }
            }
            this.applyOrder(canonical, null);
          } catch (e) {
            console.debug('Error processing onCheckboxToggle delayed work', e);
          }
        }, 0);
      } catch (e) {}
    } catch (e) {}
  }

  /**
   * Compute the ordered map features options based on current selection and custom order.
   * Compute the ordered list of mapFeatures options so that map-layer items follow the
   * underlying mapFeatures array order (as stored in the FilterService). Non map-layer
   * options are kept first in their original order.
   *
   * This method separates the map-layer options from other options, then orders
   * the map-layer options based on their order in the current filters. Any map-layer
   * options not present in the filters (e.g., unselected ones) are appended at the end
   * in their original order. Finally, it combines the non-map options with the ordered
   * map-layer options to form the final ordered list.
   */
  private computeOrderedOptions(): void {
    const allOptions = this.mapFeaturesOptions || [];
    const nonMapOptions = allOptions.filter((o) => !/^map-layer-\d+$/.test(String(o.key)));
    const mapLayerOptions = allOptions.filter((o) => /^map-layer-\d+$/.test(String(o.key)));

    const mapByKey = new Map<string, any>();
    mapLayerOptions.forEach((o) => mapByKey.set(String(o.key), o));

    const currentFilters = this.myFilterService.getFilters();
    const orderedKeys: string[] = Array.isArray(currentFilters.mapFeatures)
      ? currentFilters.mapFeatures.filter((f: string) => /^map-layer-\d+$/.test(f))
      : this.getSelectedMapLayerKeys();

    const orderedMapLayers: any[] = [];
    const used = new Set<string>();

    console.debug('[map-order] computeOrderedOptions called');
    console.debug(
      '[map-order] allOptions keys=',
      allOptions.map((o) => o.key)
    );
    console.debug('[map-order] currentFilters.mapFeatures (raw)=', currentFilters.mapFeatures);
    console.debug('[map-order] orderedKeys (derived)=', orderedKeys);

    orderedKeys.forEach((k) => {
      const opt = mapByKey.get(k);
      if (opt) {
        orderedMapLayers.push(opt);
        used.add(k);
      } else {
        console.debug('[map-order] computeOrderedOptions: key in filters not found in options=', k);
      }
    });

    mapLayerOptions.forEach((o) => {
      if (!used.has(String(o.key))) orderedMapLayers.push(o);
    });

    const selected = orderedMapLayers.filter((o) => used.has(String(o.key)));
    const unselected = orderedMapLayers.filter((o) => !used.has(String(o.key)));
    const selectedTopFirst = selected.slice().reverse();

    this.orderedMapFeaturesOptions = [...nonMapOptions, ...selectedTopFirst, ...unselected];

    console.debug(
      '[map-order] orderedMapFeaturesOptions keys=',
      this.orderedMapFeaturesOptions.map((o) => o.key)
    );
  }

  /**
   * Recompute the ordered options whenever the base class rebuilds the map
   * feature options from the API response.
   *
   * Without this the drawer keeps rendering the pre-API option list (and its
   * static ordering) until some unrelated filter change happens to trigger a
   * recompute.
   */
  protected onMapFeaturesOptionsUpdated(): void {
    try {
      this.computeOrderedOptions();
    } catch (e) {
      console.debug('Error recomputing ordered options after map feature options update', e);
    }
  }

  /**
   * Enforce the saved map layer selection and order from local storage.
   *
   * Enforces local storage selection/order over API updates for mapFeatures
   * This subscriber runs whenever mapFeatures filters change (e.g. when API/geojson responses come back)
   * and will overwrite the service filters with the saved custom order/selection if they differ.
   * Uses both saved order and saved selection to determine what to apply,
   */
  private enforceSavedMapLayerSelection = (): void => {
    try {
      if ((this as any).suppressLocalPersistence) return;
      if (!this.site) return;
      if (!this.mapFeaturesOptions || this.mapFeaturesOptions.length === 0) return;

      const availableKeys = (this.mapFeaturesOptions || []).map((o) => String(o.key));

      const savedOrder = this.loadCustomMapLayerOrder();
      const savedSelection = this.loadMapLayerSelectionFromLocalStorage();

      let toApply: string[] | null = null;
      if (savedOrder && savedOrder.length) {
        const filtered = savedOrder.filter((k) => availableKeys.includes(k));
        if (filtered.length) toApply = filtered;
      }

      if (!toApply && savedSelection && savedSelection.length) {
        const filtered = savedSelection.filter((k) => availableKeys.includes(k));
        if (filtered.length) toApply = filtered;
      }

      if (toApply && toApply.length) {
        const currentFilters = this.myFilterService.getFilters();
        const currentMapLayers: string[] = Array.isArray(currentFilters.mapFeatures)
          ? currentFilters.mapFeatures.filter((f: string) => /^map-layer-\d+$/.test(f))
          : this.getSelectedMapLayerKeys();

        console.debug('enforceSavedMapLayerSelection: toApply=', toApply, ' current=', currentMapLayers);
        if (!this.arraysEqual(currentMapLayers, toApply)) {
          this.applyOrder(toApply, null);
        }
      }
    } catch (e) {
      console.debug('Error enforcing saved map layer selection/order', e);
    }
  };

  /**
   * Get the currently selected map layer keys in the order they appear in the form value.
   * Supports both array and object formats of the form value.
   *
   * Subscribes to FilterService changes to recompute ordering whenever mapFeatures change,
   * and also recomputes when the reactive form changes (checkbox toggles / initial form build).
   *
   * Respects the suppressLocalPersistence flag from the base class to avoid
   * interfering during programmatic updates.
   *
   * Debounces rapid changes to avoid excessive recomputations.
   *
   * Display a spinner when a reorder/refresh is in progress.
   *
   * Adds no-op here; subclasses can wire control-level subscriptions when the form is
   * (re)initialized via the onFilterFormInitialized hook override below.
   *
   */
  ngOnInit(): void {
    try {
      if (typeof super.ngOnInit === 'function') super.ngOnInit();
    } catch (e) {}

    // Initialize default map features options without the 'observation' layer
    // overriding the base class default.
    // this has the effect of removing the 'observation' layer from the options
    // on the left hand drawer.
    this.defaultMapFeaturesOptions = this.compileOptions<string>('mapFeatures', [
      { key: 'monitor', value: 'Monitoring Stations' },
      { key: 'observation', value: 'Observations' },
    ]);

    setTimeout(() => this.computeOrderedOptions(), 0);

    this.myFilterService.subscribe(() => {
      this.computeOrderedOptions();
    }, ['mapFeatures']);

    this.myFilterService.subscribe(this.enforceSavedMapLayerSelection, ['mapFeatures']);

    if (this.filterForm && this.filterForm.valueChanges) {
      this.filterForm.valueChanges.pipe(auditTime(0)).subscribe(() => this.computeOrderedOptions());
    }

    if (this.filterForm && this.filterForm.valueChanges) {
      this.filterForm.valueChanges.pipe(auditTime(50)).subscribe((val) => {
        try {
          console.debug('[drawer-debug] form.mapFeatures value=', val?.mapFeatures);
          console.debug('[drawer-debug] filterService.mapFeatures=', this.myFilterService.getFilters().mapFeatures);
        } catch (e) {
          console.debug('[drawer-debug] error reading mapFeatures', e);
        }
      });
    }

    this.subscribeOnObservable(this.mapService.reorderBusy$, (val: boolean) => {
      this.reorderBusy = !!val;
    });

    try {
      this.subscribeOnObservable(this.mapService.layerInit$, (payload: { siteId: string; state: string }) => {
        try {
          if (!payload || payload.siteId !== this.site?.id) return;
          if (payload.state === 'initializing') {
            (this as any).suppressLocalPersistence = true;
          } else if (payload.state === 'initialized') {
            (this as any).suppressLocalPersistence = false;
          }
        } catch (e) {
          console.debug('Error handling mapService.layerInit$', e);
        }
      });
    } catch (e) {}

    if (this.filterForm && this.filterForm.valueChanges) {
      this.filterForm.valueChanges.pipe(auditTime(200)).subscribe(() => {
        try {
          const selected = this.getSelectedMapLayerKeys();
          this.saveMapLayerSelectionToLocalStorage(selected);
        } catch (e) {
          console.debug('Error persisting map layer selection', e);
        }
      });
    }
  }

  /**
   * Hook called by the base class whenever the filter form is initialized or recreated.
   * Use this to wire any control-level subscriptions that must be reattached each time.
   * Ensures that we do not have a stale subscription attached to the prior FormGroup.
   * This is called after the form is initialized and before any value changes are processed.
   *
   * Subclasses can override this to wire control-level subscriptions or other initialization logic.
   * Will disable the Apply button if the required controls are not present.
   */
  protected onFilterFormInitialized(): void {
    if (this.environmentalControlsSub) {
      this.environmentalControlsSub.unsubscribe();
      this.environmentalControlsSub = null;
    }

    const sourcesControl = this.filterForm?.get('selectedEnvironmentalDataSources');
    const groupsControl = this.filterForm?.get('selectedEnvironmentalDataSourceDashGroups');

    if (!sourcesControl || !groupsControl) {
      this.environmentalApplyDisabled = true;
      return;
    }

    this.environmentalControlsSub = combineLatest([
      sourcesControl.valueChanges.pipe(startWith(sourcesControl.value)),
      groupsControl.valueChanges.pipe(startWith(groupsControl.value)),
    ])
      .pipe(debounceTime(50))
      .subscribe(([sVal, gVal]) => {
        const current = this.myFilterService.getFilters();

        const curSources = Array.isArray(current.selectedEnvironmentalDataSources)
          ? current.selectedEnvironmentalDataSources
          : current.selectedEnvironmentalDataSources
          ? [current.selectedEnvironmentalDataSources]
          : ['all'];

        const curGroups = Array.isArray(current.selectedEnvironmentalDataSourceDashGroups)
          ? current.selectedEnvironmentalDataSourceDashGroups
          : current.selectedEnvironmentalDataSourceDashGroups
          ? [current.selectedEnvironmentalDataSourceDashGroups]
          : ['all'];

        const selSources = Array.isArray(sVal) ? sVal : sVal ? [sVal] : [];
        const selGroups = Array.isArray(gVal) ? gVal : gVal ? [gVal] : [];

        const normSelSources = selSources.length ? selSources : ['all'];
        const normSelGroups = selGroups.length ? selGroups : ['all'];

        this.environmentalApplyDisabled =
          this.arraysShallowEqualUnordered(normSelSources, curSources) &&
          this.arraysShallowEqualUnordered(normSelGroups, curGroups);
      });
  }

  /**
   * Apply the currently selected environmental data sources / groups to the FilterService.
   * This is triggered by the "Apply Filters" button in the drawer.
   * Will set applyingEnvironmentalFilters to false on the next tick so the UI updates after filters applied.
   */
  public applySelectedEnvironmentalFilters(): void {
    try {
      const rawSources = this.filterForm?.value?.selectedEnvironmentalDataSources;
      const rawGroups = this.filterForm?.value?.selectedEnvironmentalDataSourceDashGroups;

      const sources = Array.isArray(rawSources) ? rawSources : rawSources ? [rawSources] : [];
      const groups = Array.isArray(rawGroups) ? rawGroups : rawGroups ? [rawGroups] : [];

      try {
        this.applyingEnvironmentalFilters = true;
        this.environmentalApplyDisabled = true;
        const emptySelection = this.isReactDashboardRoute() ? [] : ['all'];
        this.myFilterService.setFilters({
          selectedEnvironmentalDataSources: sources.length ? sources : emptySelection,
          selectedEnvironmentalDataSourceDashGroups: groups.length ? groups : emptySelection,
        });
      } finally {
        setTimeout(() => {
          this.applyingEnvironmentalFilters = false;
        }, 0);
      }
    } catch (e) {
      console.debug('Error applying selected environmental filters', e);
    }
  }

  public onEnvironmentalDataSourcesSelectionChange(event: any): void {
    return this.enforceAllSelectionSemantics('selectedEnvironmentalDataSources', event);
  }

  public onEnvironmentalDataSourceDashGroupsSelectionChange(event: any): void {
    return this.enforceAllSelectionSemantics('selectedEnvironmentalDataSourceDashGroups', event);
  }

  public onContributingOrganisationsSelectionChange(event: any): void {
    return this.enforceAllSelectionIsExclusive('selectedContributingOrganisations', event);
  }

  public onContributingProjectListItemsSelectionChange(event: any): void {
    return this.enforceAllSelectionIsExclusive('selectedContributingProjectListItems', event);
  }

  private enforceAllSelectionIsExclusive(controlName: string, event: any): void {
    const ctrl = this.filterForm?.get(controlName);
    if (!ctrl || event?.isUserInput === false) return;

    const optionValue = event?.source?.value;
    const current = Array.isArray(ctrl.value) ? [...ctrl.value] : ctrl.value ? [ctrl.value] : [];

    if (optionValue === 'all' && event?.source?.selected) {
      ctrl.setValue(['all']);
      return;
    }

    if (optionValue !== 'all' && event?.source?.selected) {
      ctrl.setValue(Array.from(new Set([...current, optionValue])).filter((value) => value !== 'all'));
      return;
    }

    if (current.length === 0) {
      ctrl.setValue(['all']);
    }
  }

  /**
   * Shared helper to enforce "all" selection semantics for a multi-select control.
   * - If the user selects 'all' (it wasn't selected before) -> set only ['all']
   * - If 'all' was selected and the user selects other items -> remove 'all'
   * - Otherwise remove any stray 'all' values
   */
  private enforceAllSelectionSemantics(controlName: string, event: any): void {
    const ctrl = this.filterForm?.get(controlName);
    if (!ctrl) return;

    const prev = Array.isArray(ctrl.value) ? [...ctrl.value] : ctrl.value ? [ctrl.value] : [];
    const next = Array.isArray(event?.value) ? [...event.value] : event?.value ? [event.value] : [];

    const prevHasAll = prev.includes('all');
    const nextHasAll = next.includes('all');

    // Cases to handle:
    // 1) When there were no previous selections and 'all' is selected -> set ['all']
    // 2) When 'all' was previously selected and the user selects non-'all' items -> remove 'all'
    // 3) When non-'all' items were selected and the user selects 'all' -> set ['all'] (deselect others)

    const nextHasNonAll = next.some((v) => v !== 'all');

    // If the user clicked the 'all' option (MatOptionSelectionChange) detect that first
    // and make it exclusive. Some event shapes provide an `option` object describing the
    // MatOption that changed; use that when available.
    const userClickedAllOption = !!(
      event &&
      event.option &&
      event.option.value === 'all' &&
      event.option.selected === true
    );

    // Case 1 & 3: any transition that introduces 'all' while it wasn't previously present
    // should result in exclusive ['all'] selection. Prefer direct option click detection
    // but fall back to comparing prev/next arrays when option metadata is not present.
    if (userClickedAllOption || (nextHasAll && !prevHasAll)) {
      ctrl.setValue(['all']);
      return;
    }

    // Case 2: if 'all' was selected and user has now selected other explicit items,
    // remove 'all' and keep the explicit selections.
    if (prevHasAll && nextHasNonAll) {
      const filtered = next.filter((v) => v !== 'all');
      ctrl.setValue(filtered);
      return;
    }

    // If the incoming value is exactly ['all'] (no-op), leave as is.
    if (nextHasAll && next.length === 1) return;

    // Default: ensure 'all' is not present (clean stray 'all') and set the cleaned value.
    const cleaned = next.filter((v) => v !== 'all');
    ctrl.setValue(cleaned);
  }

  ngOnDestroy(): void {
    try {
      if (typeof super.ngOnDestroy === 'function') super.ngOnDestroy();
    } catch (e) {}

    if (this.environmentalControlsSub) {
      this.environmentalControlsSub.unsubscribe();
      this.environmentalControlsSub = null;
    }
  }

  /**
   * Compare two string arrays for equality regardless of order.
   */
  private arraysShallowEqualUnordered(a: string[] | null | undefined, b: string[] | null | undefined): boolean {
    const aa = Array.isArray(a) ? a.slice() : [];
    const bb = Array.isArray(b) ? b.slice() : [];
    if (aa.length !== bb.length) return false;
    aa.sort();
    bb.sort();
    for (let i = 0; i < aa.length; i++) if (aa[i] !== bb[i]) return false;
    return true;
  }

  /**
   * Get the currently active (checked) map feature options in the current display order.
   *
   * @returns An array of active map feature options in the current display order.
   */
  getActiveMapFeatureOptions(): FilterDrawerReorderableLayoutOptions<string> {
    const opts = this.getOrderedOptions();
    if (!this.filterForm) return [];
    const mv = this.filterForm.value?.mapFeatures;
    if (!mv) return [];

    if (Array.isArray(mv)) {
      return opts.filter((o) => mv.includes(o.key));
    }

    if (typeof mv === 'object') {
      return opts.filter((o) => !!mv[o.key]);
    }

    return [];
  }

  /**
   * Get the currently inactive (unchecked) map feature options in the current display order.
   *
   * @returns An array of inactive map feature options.
   */
  getInactiveMapFeatureOptions(): FilterDrawerReorderableLayoutOptions<string> {
    const opts = this.getOrderedOptions();
    if (!this.filterForm) return opts;
    const mv = this.filterForm.value?.mapFeatures;
    if (!mv) return opts;

    if (Array.isArray(mv)) {
      return opts.filter((o) => !mv.includes(o.key));
    }

    if (typeof mv === 'object') {
      return opts.filter((o) => !mv[o.key]);
    }

    return opts;
  }

  markerFeatureKeys: Set<string> = new Set(['monitor', 'observation']);

  /**
   * Canonical display order for marker map features.
   *
   * The drawer's marker section is rendered in this order regardless of the
   * order the options happen to be built in (static defaults vs API response),
   * so "Observations" is always shown before "Monitoring Stations".
   */
  private readonly markerFeatureOrder: string[] = ['observation', 'monitor'];

  /**
   * Get the sort rank for a marker feature key, using the canonical marker
   * order. Unknown keys sort after the known marker features.
   *
   * @param key The marker feature key to rank.
   * @returns The sort rank for the key.
   */
  private getMarkerFeatureRank(key: any): number {
    const index = this.markerFeatureOrder.indexOf(String(key));
    return index === -1 ? this.markerFeatureOrder.length : index;
  }

  /**
   * Check if a given feature key corresponds to a marker feature.
   *
   * Checks the marker feature keys (those that render Leaflet markers rather than geojson layers)
   * @param key The feature key to check.
   * @returns True if the key is a marker feature key, false otherwise.
   */
  isMarkerFeatureKey(key: string): boolean {
    return this.markerFeatureKeys.has(key);
  }

  /**
   * Get all marker feature options (active + inactive) in the canonical marker
   * display order (Observations before Monitoring Stations), regardless of the
   * order in which the options were loaded.
   *
   * @returns An array of all marker feature options in the canonical display order.
   */
  getAllMarkerFeatureOptions(): FilterDrawerLayoutOptions<string> {
    const opts = this.getOrderedOptions();
    return opts
      .filter((o) => this.isMarkerFeatureKey(String(o.key)))
      .sort((a, b) => this.getMarkerFeatureRank(a.key) - this.getMarkerFeatureRank(b.key));
  }

  /**
   * Get active map feature options that are not marker features.
   *
   * @returns An array of active map feature options that are not marker features.
   */
  getActiveOtherFeatureOptions(): FilterDrawerReorderableLayoutOptions<string> {
    return this.getActiveMapFeatureOptions().filter((o) => !this.isMarkerFeatureKey(String(o.key)));
  }

  /**
   * Get inactive map feature options that are not marker features.
   *
   * @returns An array of inactive map feature options that are not marker features.
   */
  getInactiveOtherFeatureOptions(): FilterDrawerReorderableLayoutOptions<string> {
    return this.getInactiveMapFeatureOptions().filter((o) => !this.isMarkerFeatureKey(String(o.key)));
  }

  /**
   * Move a layer up in the display order.
   *
   * Swapped behavior: Up button now moves the item visually down
   *
   * @param layerKey The key of the layer to move up.
   * @param event The mouse event that triggered the action.
   * @returns
   */
  moveLayerUp(layerKey: string, event?: Event): void {
    if (event) event.stopPropagation();
    if (this.reorderLock) return;
    this.reorderLock = true;
    try {
      console.debug(
        '[map-order] moveLayerUp(clicked-as-down) key=',
        layerKey,
        'label=',
        this.getMapFeatureLabel(layerKey)
      );
      const displayedSelected = this.getActiveOtherFeatureOptions().map((o) => String(o.key));
      console.debug('[map-order] displayed selected order=', displayedSelected);
      const i = displayedSelected.indexOf(layerKey);
      if (i === -1 || i >= displayedSelected.length - 1) {
        console.debug('[map-order] moveLayerUp(swapped): index out of range or already bottom, no-op');
        return;
      }
      const copyDisplay = [...displayedSelected];
      const tmp = copyDisplay[i + 1];
      copyDisplay[i + 1] = copyDisplay[i];
      copyDisplay[i] = tmp;
      const canonical = copyDisplay.slice().reverse();
      console.debug('[map-order] new canonical order after swapped-moveUp=', canonical);
      this.applyOrder(canonical, null);
    } finally {
      this.reorderLock = false;
    }
  }

  /**
   * Move a layer down in the display order.
   *
   * Swapped behavior: Down button now moves the item visually up.
   *
   * @param layerKey The key of the layer to move down.
   * @param event The mouse event that triggered the action.
   * @returns
   */
  moveLayerDown(layerKey: string, event?: Event): void {
    if (event) event.stopPropagation();
    if (this.reorderLock) return;
    this.reorderLock = true;
    try {
      console.debug(
        '[map-order] moveLayerDown(clicked-as-up) key=',
        layerKey,
        'label=',
        this.getMapFeatureLabel(layerKey)
      );
      const displayedSelected = this.getActiveOtherFeatureOptions().map((o) => String(o.key));
      console.debug('[map-order] displayed selected order=', displayedSelected);
      const i = displayedSelected.indexOf(layerKey);
      if (i <= 0 || i === -1) {
        console.debug('[map-order] moveLayerDown(swapped): index out of range or already top, no-op');
        return;
      }
      const copyDisplay = [...displayedSelected];
      const tmp = copyDisplay[i - 1];
      copyDisplay[i - 1] = copyDisplay[i];
      copyDisplay[i] = tmp;

      const canonical = copyDisplay.slice().reverse();
      console.debug('[map-order] new canonical order after swapped-moveDown=', canonical);
      this.applyOrder(canonical, null);
    } finally {
      this.reorderLock = false;
    }
  }

  /**
   * Save the current order of selected map layers.
   *
   * This method retrieves the selected map layer keys in their current order,
   * saves this order using the saveCustomMapLayerOrder method, and updates the
   * FilterService to reflect this new order while preserving any non-map-layer filters.
   *
   * Recomputes computeOrderedOptions and request map to reapply ordering.
   */
  saveOrder(): void {
    const selected = this.getSelectedMapLayerKeys();
    this.applyOrder(selected, null);
  }

  /**
   * Reset the map layer order to the original default order.
   *
   * This method clears any custom order saved using resetCustomMapLayerOrder,
   * then rebuilds the mapFeatures in the FilterService to reflect the original
   * ordering while preserving any non-map-layer filters. Finally, it recomputes the ordered options
   * to update the display.
   *
   * Persists the reset selection to localStorage.
   *
   * Requests the Map component to reapply the API layer ordering immediately.
   *
   * Clears any custom order saved in localStorage for the current site.
   *
   * Note: This does not reload the map layers from the API; it only resets the order.
   */
  resetOrder(): void {
    console.debug('[map-order] resetOrder clicked');
    this.resetCustomMapLayerOrder();
    const newFilters = this.myFilterService.getFilters().mapFeatures.filter((f: string) => /^map-layer-\d+/.test(f));
    console.debug('[map-order] resetting to order=', newFilters);
    this.applyOrder(newFilters, null, { saveCustomOrder: false, saveLocalSelection: true });
  }

  /**
   * Reorder the map features while preserving non-map-layer filters.
   *
   * @param newMapLayerOrder An array of map layer keys in the desired order.
   * @returns A new array of map features with the updated order.
   */
  private reorderPreserveOtherFilters(newMapLayerOrder: string[]): string[] {
    const current = this.myFilterService.getFilters().mapFeatures;
    const others = current.filter((f: string) => !/^map-layer-\d+/.test(f));
    return [...others, ...newMapLayerOrder];
  }

  /**
   * Return selected map layer keys in a stable, display order.
   * Prefer the authoritative order stored in FilterService (array form). If that's not
   * available, fall back to the form-derived list.
   *
   * Provides a fallback to ensure a consistent order for operations like reordering.
   */
  private getSelectedMapLayerKeysOrdered(): string[] {
    const currentFilters = this.myFilterService.getFilters();
    const mf = currentFilters.mapFeatures;
    if (Array.isArray(mf)) {
      return mf.filter((f: string) => /^map-layer-\d+$/.test(f));
    }
    return this.getSelectedMapLayerKeys();
  }

  /**
   * Check if a map feature is selected.
   *
   * @param key The feature key to check.
   * @returns True if the feature is selected, false otherwise.
   */
  public isSelected(key: string): boolean {
    return this.getSelectedMapLayerKeysOrdered().includes(key);
  }

  /**
   * Check if a map feature is the first selected.
   * @param key The feature key to check.
   * @returns True if the feature is the first selected, false otherwise.
   */
  public isFirstSelected(key: string): boolean {
    const ls = this.getSelectedMapLayerKeysOrdered();
    return ls.length > 0 && ls[0] === key;
  }

  /**
   * Check if a map feature is the last selected.
   * @param key The feature key to check.
   * @returns True if the feature is the last selected, false otherwise.
   */
  public isLastSelected(key: string): boolean {
    const ls = this.getSelectedMapLayerKeysOrdered();
    return ls.length > 0 && ls[ls.length - 1] === key;
  }

  /**
   * Check if a map feature can be moved up in the display order.
   * @param key The feature key to check.
   * @returns True if the feature can be moved up, false otherwise.
   */
  public canMoveUp(key: string): boolean {
    const options = this.getActiveOtherFeatureOptions();

    const currentIndex = options.findIndex((o) => o.key === key);
    if (currentIndex > 0) {
      const previous = options[currentIndex - 1];
      const current = options[currentIndex];

      return (
        previous.defaultZIndex !== undefined &&
        current.maxZIndex !== undefined &&
        previous.defaultZIndex < current.maxZIndex
      );
    }
    return false;
  }

  /**
   * Check if a map feature can be moved down in the display order.
   * @param key The feature key to check.
   * @returns True if the feature can be moved down, false otherwise.
   */
  public canMoveDown(key: string): boolean {
    const options = this.getActiveOtherFeatureOptions();

    const currentIndex = options.findIndex((o) => o.key === key);
    if (currentIndex < options.length - 1) {
      const next = options[currentIndex + 1];
      const current = options[currentIndex];

      return (
        next.defaultZIndex !== undefined && current.minZIndex !== undefined && next.defaultZIndex > current.minZIndex
      );
    }
    return false;
  }

  /**
   * Check if a map feature is the last in the currently displayed (active) list.
   *
   * Uses the display order (getActiveMapFeatureOptions) rather than the authoritative
   * FilterService ordering so disabled states for inline controls match what the
   * user sees in the UI.
   *
   * @param key The feature key to check.
   * @returns True if the feature is the last in the displayed list, false otherwise.
   */
  public isLastDisplayed(key: string): boolean {
    const displayed = this.getActiveOtherFeatureOptions().map((o) => String(o.key));
    return displayed.length > 0 && displayed[displayed.length - 1] === String(key);
  }

  /**
   * Get the count of currently selected map layers.
   * @returns The count of currently selected map layers.
   */
  public selectedCount(): number {
    return this.getSelectedMapLayerKeysOrdered().length;
  }

  /**
   * Return the human-visible label for a map feature key by consulting the
   * computed ordered options (fall back to the raw options list). Returns the
   * key itself if no matching option is found.
   */
  public getMapFeatureLabel(key: string): string {
    const opts = this.getOrderedOptions();
    const found = opts.find((o) => String(o.key) === String(key));
    return found ? String(found.value) : String(key);
  }

  public getCSV(): void {
    const { fromDate, toDate, selectedLocations, searchTerm } = this.myFilterService.getFilters();

    this.chartService
      .getObservationCsv(
        moment(fromDate).format('YYYY-MM-DD'),
        moment(toDate).format('YYYY-MM-DD'),
        selectedLocations,
        searchTerm
      )
      .subscribe({
        next: (response: any) => {
          const csvText = typeof response === 'string' ? response : response?.csv || '';
          if (!csvText) {
            console.warn('No CSV content returned from observation CSV endpoint');
            return;
          }

          // Help Excel recognize UTF-8 by adding a BOM at the start.
          const bom = '\uFEFF';
          const blob = new Blob([bom + csvText], { type: 'text/csv;charset=utf-8;' });

          const invisibleAnchor = document.createElement('a');
          invisibleAnchor.href = URL.createObjectURL(blob);
          invisibleAnchor.setAttribute('download', `observations-${new Date().toLocaleDateString()}.csv`);
          invisibleAnchor.click();
          URL.revokeObjectURL(invisibleAnchor.href);
        },
        error: (err: any) => {
          console.error('Error downloading observations CSV', err);
        },
      });
  }
}
